<template>
  <div
    class="bed-visualizer critical-component-hide"
    :class="classes"
  >
    <div
      ref="controls"
      class="bed-visualizer__controls body-2"
    >
      <div
        class="badge-and-title">
        <badge
          block-class="bed-visualizer"
          :label="badgeText"
        />

        <h1>Design your bed</h1>
      </div>

      <div class="bed-visualizer-exit-buttons-container">
        <button
          class="bed-visualizer__button bed-visualizer__button-exit"
          @click="handleExitClick"
        >
          <span
            class="bed-visualizer__button-text"
            v-text="$string('bedVisualizer.exit')"
          />
          <span v-html="$icon('misc.close')" />
        </button>
        <button
          class="bed-visualizer__button bed-visualizer__button-exit bed-visualizer-clear-bed-button"
          @click="handleClearBed"
        >
          <span
            class="bed-visualizer__button-text"
            v-text="'Clear Bed / Start Over'"
          />
        </button>
      </div>
    </div>

    <bed-visualizer-confirmation
      v-if="isMobile && activePage === 'Confirmation'"
      :enable-save="liquid.settings.enable_save"
      :is-mobile="true"
      :items-count="itemsCount"
      :items-subtotal="itemsSubtotal"
    >
      <template #image>
        <bed-visualizer-image
          :base-image="liquid.base_image"
          :base-stack-order="liquid.base_stack_order"
          :base-pillow-image="liquid.base_pillow_image"
          :base-pillow-stack-order="liquid.base_pillow_stack_order"
          :base-sheet-image="liquid.base_sheet_image"
          :base-sheet-stack-order="liquid.base_sheet_stack_order"
        />
      </template>
    </bed-visualizer-confirmation>

    <div
      v-else
      :class="{
        'is-collapsed': drag.isDrawerOpen === false
      }"
      class="bed-visualizer__inner"
      @mousedown="onDragStart"
      @mousemove="onDrag"
      @mouseup="onDragEnd"
    >
      <div
        ref="media"
        class="bed-visualizer__media"
      >
        <bed-visualizer-image
          :base-image="liquid.base_image"
          :base-stack-order="liquid.base_stack_order"
          :base-pillow-image="liquid.base_pillow_image"
          :base-pillow-stack-order="liquid.base_pillow_stack_order"
          :base-sheet-image="liquid.base_sheet_image"
          :base-sheet-stack-order="liquid.base_sheet_stack_order"
        />
      </div>

      <div
        ref="drawer"
        class="bed-visualizer__drawer"
      >
        <button
          ref="drawerExpand"
          class="bed-visualizer__drawer-expand"
          @touchend="onDragEnd"
          @touchmove="onDrag"
          @touchstart="onDragStart"
          @click="toggleDrawer"
        >
          <span class="visually-hidden" />
        </button>
        <template v-for="(step, index) in steps">
          <bed-visualizer-customize
            v-if="step.name === activePage"
            :key="step.name"
            :active-flow="config.activeFlow"
            :collection-handle="step.collection"
            :filters-open="filtersOpen"
            :is-mobile="isMobile"
            :limit="step.limit"
            :next-step-name="getNextPageName(step.name)"
            :prev-step-name="getPrevPageName(step.name)"
            :stack-orders="step.stackOrders"
            :step-count="index + 1"
            :step-name="step.name"
            :total-step-count="steps.length"
            @option-change="handleOptionChange"
            @prev-page="goToPrevPage"
            @next-page="goToNextPage"
            @filter-toggle-clicked="toggleFilterState"
          />
        </template>

        <bed-visualizer-collections
          v-if="activePage === 'Collections'"
          :collections="liquid.collection_blocks"
          :categories="collectionCategories"
          @collection-change="handleCollectionChange"
        />

        <bed-visualizer-review
          v-if="activePage === 'Review'"
          :add-ons="addOns"
          :customize-page-order="customizeStepsOrder"
          :enable-save="liquid.settings.enable_save"
          @option-edit="handleOptionEdit"
          @option-change="handleOptionChange"
        />

        <bed-visualizer-confirmation
          v-if="!isMobile && activePage === 'Confirmation'"
          :enable-save="liquid.settings.enable_save"
          :is-mobile="false"
          :items-count="itemsCount"
          :items-subtotal="itemsSubtotal"
        />

        <div
          v-if="activePage !== 'Confirmation'"
          class="bed-visualizer__summary"
        >
          <template v-if="isMobile && filtersOpen">
            <btn
              class="bed-visualizer__cta"
              type="button"
              :icon="false"
              :label="ctaText"
              :modifiers="[
                'block',
                'center',
              ]"
              @click="handleApplyFilters"
            />
          </template>

          <template v-else>
            <div
              class="bed-visualizer__subtotal body-2"
              v-text="subtotalString"
            />

            <btn
              v-if="this.activePage === 'Review'"
              class="bed-visualizer__cta"
              type="button"
              :disabled="ctaDisabled"
              :icon="false"
              :label="ctaText"
              :modifiers="[
                'center',
                'small',
                ctaDisabled ? 'disabled' : 'secondary',
              ]"
              @click="handleCtaClick"
            />

            <template v-for="(step, index) in steps">
              <nav-btn
                v-if="step.name === activePage && !filtersOpen"
                class="bed-visualizer__action"
                :block-class="'bed-visualizer'"
                :is-mobile="isMobile"
                :next-step="getNextPageName(step.name)"
                :prev-step="getPrevPageName(step.name)"
                @prev-page="goToPrevPage"
                @next-page="goToNextPage"
              />
            </template>
          </template>
        </div>
      </div>
    </div>

    <teleport to="body">
      <transition name="fade">
        <visualizer-exit-modal
          v-show="showExitModal && activePage !== 'Confirmation'"
          :enable-save="liquid.settings.enable_save"
          @save="false"
          @close-exit-modal="showExitModal = false"
          @close-bed-visualizer="exitVisualizer('modal')"
        />
      </transition>
    </teleport>
  </div>
</template>

<script>

/**
 * Vue: Bed visualizer (bed-visualizer)
 * -----------------------------------------------------------------------------
 * Displays contents of bed visualizer.
 *
 * TODO: Replace @save with real save functionality
 */
import { nextTick } from 'vue'
import { mapActions, mapState } from 'vuex'
import BedVisualizerImage from './bed-visualizer-image'
import BedVisualizerCollections from './pages/bed-visualizer.collections'
import BedVisualizerCustomize from './pages/bed-visualizer.customize'
import BedVisualizerReview from './pages/bed-visualizer.review'
import BedVisualizerConfirmation from './pages/bed-visualizer.confirmation'
import Btn from '~global/btn/btn'
import VisualizerExitModal from './visualizer-exit-modal/visualizer-exit-modal'
import { queryProductById } from '~/helpers/query'
import { clearAllBodyScrollLocks } from 'body-scroll-lock'
import { convertToHandle } from '~/helpers/convert'
import { queryProducts } from '~/helpers/query'
import { filterVariants, formatVariant } from './bed-visualizer-helpers'
import timings from '~/config/timings'
import Badge from '~global/badge/badge'
import NavBtn from './nav-btn'

const steps = require('./customize-steps')
const collectionCategories = require('./collection-categories')

export default {
  name: 'BedVisualizer',

  components: {
    BedVisualizerImage,
    BedVisualizerCollections,
    BedVisualizerCustomize,
    BedVisualizerReview,
    BedVisualizerConfirmation,
    Btn,
    NavBtn,
    VisualizerExitModal,
    Badge,
  },

  props: {
    liquid: {
      type: Object,
      default: () => ({
        settings: [],
        addOnBlocks: [],
        base_image: {},
        base_sheet_image: {},
        base_pillow_image: {},
        baseStackOrder: 1,
        basePillowStackOrder: 3,
        baseSheetStackOrder: 5,
        collection_blocks: [],
      }),
    },
  },

  data() {
    return {
      badgeText: 'BED VISUALIZER',
      activePage: '',
      activeFlow: '',
      addOns: {},
      drag: {
        animating: false,
        dragging: false,
        isDrawerOpen: false,
        startY: null,
      },
      error: '',
      filtersOpen: false,
      gtmEvent: 'BedStylerFunnel',
      isMobile: true,
      timeout: {},
      pageInitialized: false,
      showExitModal: false,
      steps: steps,
      collectionCategories: collectionCategories,
    }
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      config: (state) => state['bed-visualizer'].config,
      screen: (state) => state.index.screen,
    }),

    /**
     * Compute classes.
     */
    classes() {
      return [`bed-visualizer--${convertToHandle(this.activePage)}`]
    },

    /**
     * Compute CTA text.
     */
    ctaText() {
      const nextPage = this.getNextPageName(this.activePage)
      if (this.activePage === 'Review') {
        return this.config.activeFlow === 'collections' ?
          this.$string('bedVisualizer.collection.cta') :
          this.$string('bedVisualizer.review.cta')
      }

      if (this.activePage === 'Collections') {
        return this.itemsCount ?
          this.$string('bedVisualizer.collection.reviewCta') :
          this.$string('bedVisualizer.collection.viewCta')
      }

      if (this.isMobile && this.filtersOpen) {
        return this.$string('bedVisualizer.filters.applyFilters')
      }

      if (nextPage === 'Review') {
        return this.$string('bedVisualizer.cta')
      }

      return this.$string('bedVisualizer.next_cta')
    },

    /**
     * Compute order of customize steps.
     * @returns {Array}
     */
    customizeStepsOrder() {
      return steps.map(step => step.name)
    },

    /**
     * Compute CTA disabled state.
     * - Disable CTA if user is on customize flow but not at final step.
     * @returns {Boolean}
     */
    ctaDisabled() {
      if (this.filtersOpen || this.state === 'adding') {
        return true
      }
      return this.activePage === 'Collections' ? !this.itemsCount : false
    },

    /**
     * Compute order of all pages.
     * @returns {Array}
     */
    pagesOrder() {
      if (this.config.activeFlow === 'collections') {
        return [
          'Collections',
          'Review',
          'Confirmation',
        ]
      }

      return [
        ...this.customizeStepsOrder,
        'Review',
        'Confirmation',
      ]
    },

    /**
     * Compute items count.
     * @returns {Number}
     */
    itemsCount() {
      return Object.values(this.config.selectedOptions).flat().length
    },

    /**
     * Compute items subtotal.
     * @returns {String}
     */
    itemsSubtotal() {
      const total = Object
        .values(this.config.selectedOptions).flat()
        .reduce((acc, option) => acc += option.price, 0)

      return this.$formatMoney(total)
    },

    /**
     * Compute subtotal.
     * @returns {String}
     */
    subtotalString() {
      return `(${this.itemsCount} Items): ${this.itemsSubtotal}`
    },
  },

  watch: {

    /**
     * Watch screen to recalculate dimensions.
     */
    screen() {
      this.setMobileState()
      this.setDimensions()
    },
  },

  mounted() {
    this.getAddOns()
    this.setMobileState()
    this.updateFocus()
    this.useSavedConfig()
    this.setDimensions()
    this.setInitialPage()
    this.setEventListeners()
    // Wait for useSaveConfig to be done.
    setTimeout(()=>{
      this.handleCheckState();
    },250);
  },

  unmounted() {
    this.removeEventListeners()
  },

  methods: {

    /**
     * Map Vuex actions.
     */
    ...mapActions({
      addItem: 'cart/addItem',
      closeOverlay: 'overlays/close',
      openOverlay: 'overlays/open',
      saveConfig: 'bed-visualizer/saveConfig',
      useSavedConfig: 'bed-visualizer/useSavedConfig',
      resetConfig: 'bed-visualizer/resetConfig',
      removeOption: 'bed-visualizer/removeOption',
    }),

    async getAddOns() {
      try {
        const addOns = {}
        const productHandles = this.liquid.addOnBlocks.map(block => block.handle)
        const products = await queryProducts(productHandles)

        this.liquid.addOnBlocks.forEach(block => {
          const product = products.find(product => product.handle === block.handle)

          if (!product) {
            return
          }

          let variants = product.variants

          if (!addOns[block.category]) {
            addOns[block.category] = {
              label: block.label,
              variants: [],
            }
          }

          if (block.variantTitles.trim() !== '') {
            const variantTitles = block.variantTitles.split(',').map(
              title => title.trim().toLowerCase(),
            )
            variants = variants.filter(variant => {
              return variantTitles.indexOf(variant.title.toLowerCase()) > -1
            })
          }

          if (block.enableFilters) {
            variants = filterVariants(variants, this.config.filters)
          }

          variants.forEach(variant => {
            if (!variant.available) {
              return
            }
            addOns[block.category].variants.push({
              ...formatVariant(product, variant, null, true),
              isAddOn: true,
            })
          })
        })

        this.addOns = addOns
      } catch (error) {
        reportError('[getAddOns]', error)
      }
    },

    /**
     * Set dimensions of sections
     * - Drawer must fill the remaining space of
     *  (screen height - image height - controls height)
     */
    setDimensions() {
      const {
        controls,
        drawer,
        media,
      } = this.$refs

      if (!drawer) {
        return
      }

      // Offset in px to account for the rounded corners of the drawer
      const offset = 10

      // Calculate drawer height
      const controlsHeight = controls.getBoundingClientRect().height
      const mediaHeight = media.getBoundingClientRect().height
      const drawerHeight = (window.innerHeight - controlsHeight - mediaHeight) + offset

      drawer.dataset.height = drawerHeight

      nextTick(() => {
        if (this.isMobile) {
          drawer.style.setProperty(
            'height',
            '50%',
          )
          return
        }

        if (drawer.style.height !== '') {
          drawer.style.removeProperty('height')
        }
      })
    },

    /**
     * Handle history change.
     */
    handleHistoryChange() {
      const historyPage = window.history?.state?.page

      if (historyPage && historyPage === this.activePage) {
        return
      }

      // Allow back button to exit visualizer.
      if (!historyPage || !this.pagesOrder.includes(historyPage)) {
        this.exitVisualizer(null, true)
      }

      this.activePage = historyPage
    },

    /**
     * Set event listeners.
     */
    setEventListeners() {
      window.addEventListener('popstate', this.handleHistoryChange)
    },

    /**
     * Remove event listeners.
     */
    removeEventListeners() {
      window.removeEventListener('popstate', this.handleHistoryChange)
    },

    /**
     * Set initial page.
     * - Starts on first available page
     */
    setInitialPage() {
      this.activePage = this.pagesOrder[0]

      if (this.activePage === 'Headboard' && !this.config.selectedOptions.Headboard) {
        nextTick(() => {
          this.openDrawer()
        })
      }
    },

    /**
     * Get next page name
     * @param {String} pageName - Page name.
     * @returns {String}
     */
    getNextPageName(pageName) {
      return this.pagesOrder[this.pagesOrder.indexOf(pageName) + 1]
    },

    /**
     * Get prev page name
     * @param {String} pageName - Page name.
     * @returns {String}
     */
    getPrevPageName(pageName) {
      return this.pagesOrder[this.pagesOrder.indexOf(pageName) - 1]
    },

    /**
     * Set mobile state
     */
    setMobileState() {
      this.isMobile = window.matchMedia('(orientation: portrait)').matches
    },

    /**
     * Handle add to cart submission.
     */
    async handleAddToCart() {
      clearTimeout(this.timeout)
      this.error = ''
      this.state = 'adding'

      const items = Object.values(this.config.selectedOptions).flat()

      try {
        await this.addItem(items.map((item) => ({
          id: item.id,
          properties: item.properties,
          quantity: 1,
          variant: item,
          product: { collections: item.collections },
        })))

        this.state = 'added'
        this.timeout = setTimeout(() => {
          this.state = 'ready'
        }, timings.xxl)

        this.$gtmTrack('AddtoCart', 'visualizer')
      }
      catch (error) {
        this.state = 'error'

        if (error.length) {
          this.error = error
          return
        }

        console.error('Failed to add product to cart', error)
      }
    },

    /**
     * Update focus trap and scroll lock.
     */
    updateFocus() {
      nextTick(() => {
        cnvs.EventBus.emit('overlay:update', 'bedVisualizer')
      })
    },

    /**
     * Shopify section editor select event.
     */
    shopifySectionSelect() {
    },

    /**
     * Shopify section editor deselect event.
     */
    shopifySectionDeselect() {
      this.exitVisualizer()
    },

    /**
     * Handle back click.
     */
    handleBackClick() {
      this.filtersOpen = false
      this.goToPrevPage()
    },

    /**
     * Handle exit click.
     */
    handleExitClick() {
      if (this.activePage === 'Confirmation') {
        this.exitVisualizer()
      } else {
        this.showExitModal = true
      }
    },

    /**
    * Handle clear bed.
    */
    handleClearBed() {
      // First, we reset and save state.config in window.localStorage
      this.resetConfig();
      this.saveConfig();
      // Then we send the user to Start of Bed Visualizer
      this.activePage = 'Headboard';
    },

    // This function will run at the start of the Bed Visualizer
    handleCheckState(){
      let bedVisualizerConfig = JSON.parse(window.localStorage.bedVisualizerConfig);
      const selectedOptions = bedVisualizerConfig.selectedOptions;

      Object.keys(selectedOptions).forEach(key => {
          selectedOptions[key].forEach(option => {
            (async () => {
              const productInfo = await queryProductById(option.productId);
              const selectedVariant = productInfo.variants.find(variant => variant.id === option.id);

              // If the Variant availability is false, remove it from selectedOptions
              if(selectedVariant && selectedVariant.available === false){
                this.removeOption({ key: key });
                this.saveConfig();
              }
            })().catch(e => {
              console.log(e);
            });
          })
      })

    },

    /**
     * Handle collection change and initializes selected options.
     * @param {Object} payload - Payload object.
     */
    handleCollectionChange(payload) {
      const selectedOptions = {}
      const payloadCollection = payload.selectedCollection
      const payloadProducts = payloadCollection.products
      this.config.selectedCollection = payloadCollection

      for (const product in payloadProducts) {
        selectedOptions[product] = payloadProducts[product].preSelected
      }

      this.config.selectedOptions = selectedOptions
      this.saveConfig()
    },

    /**
     * Handle option change.
     * @param {Object} payload - Payload object.
     */
    handleOptionChange(payload) {
      this.config.selectedOptions[payload.name] = payload.selectedOptions
      this.saveConfig()

      if (Object.keys(this.config.selectedOptions).length === 1 &&
          this.config.selectedOptions.Headboard
        ) {
        this.collapseDrawer()
      }

      if (payload.goToNextPage) {
        this.goToNextPage()
      }
    },

    handleOptionEdit(page) {
      if (this.config.activeFlow === 'collections') {
        this.config.activeFlow = 'collections-customize'
      }

      this.saveConfig()

      const newIndex = this.customizeStepsOrder.indexOf(page)
      this.activePage = this.pagesOrder[newIndex]
      this.pushToHistory()
    },

    /**
     * Handle CTA click.
     */
    async handleCtaClick() {
      if (this.activePage === 'Review') {
        await this.handleAddToCart()
      }

      this.goToNextPage()
    },

    /**
     * Go to prev page.
     */
    goToPrevPage() {
      const previousActivePage = this.activePage
      const newIndex = this.pagesOrder.indexOf(this.activePage) - 1

      if (newIndex < 0) {
        if (this.config.activeFlow === 'collections-customize') {
          this.config.activeFlow = 'collections'
          this.activePage = 'Collections'
          this.pushToHistory()
        } else {
          this.exitVisualizer()
        }
      } else {
        this.activePage = this.pagesOrder[newIndex]
        this.pushToHistory()
      }

      if (previousActivePage === 'Review') {
        this.collapseDrawer()
      }
    },

    /**
     * Go to next page.
     */
    goToNextPage() {
      const newIndex = this.pagesOrder.indexOf(this.activePage) + 1
      this.activePage = this.pagesOrder[newIndex]
      this.pushToHistory()

      if (this.activePage === 'Review') {
        this.openDrawer()
      }
    },

    /**
     * Add a lock to prevent click from interfering with drag (mouse)
     */
    lockDrawer() {
      this.drag.animating = true
      setTimeout(() => {
        this.drag.animating = false
      }, timings.s)
    },

    /**
     * Collapse drawer
     */
    collapseDrawer() {
      this.drag.isDrawerOpen = false
      this.setDimensions()
      this.lockDrawer()
    },

    /**
     * Exit visualizer.
     *
     * @param {String} context - Where the exit happens. Optional.
     * @param {Boolean} fromHistory - Change came from browser forward/back btn
     */
    exitVisualizer(context = '', fromHistory = false) {
      const url = 'https://www.dormify.com';
      window.location = url;
      clearAllBodyScrollLocks()

      /**
       * Push last route of visualizer-intro to browser history if the
       * route change came from user clicking exit button.
       */
      if (!fromHistory) {
        window.history.pushState({}, '', url)
      }

      if (context === 'modal') {
        this.$gtmTrack(this.gtmEvent, 'bedstyler_exit', {
          step: this.activePage,
        })
        window._learnq.push([
          'track',
          'Viewed Page',
          { PageName: 'Exit_Bed_Visualizer' },
        ])
      }
    },

    /**
     * Maximize drawer
     */
    openDrawer() {
      const { drawer } = this.$refs
      this.drag.isDrawerOpen = true
      drawer.style.removeProperty('height')
      this.lockDrawer()
    },

    toggleDrawer() {
      if (this.drag.animating) {
        return
      }

      if (this.drag.isDrawerOpen === false) {
        this.openDrawer()
      }
      else {
        this.collapseDrawer()
      }
    },

    /**
     * Handle touchstart / mousedown.
     * @param {Object} event - touchstart or mousedown events
     */
    onDragStart(event) {
      const { changedTouches, clientY, type } = event

      if (type === 'mousedown' && event.target === this.$refs.drawerExpand) {
        this.drag.dragging = true
        this.drag.startY = clientY
      }
      else if (type === 'touchstart') {
        this.drag.startY = changedTouches[0].clientY
      }
    },

    /**
     * While holding touch, change the drawer height.
     * @param {Object} event - touchstart or mousedown events
     */
    onDrag(event) {
      const { drawer } = this.$refs
      const { changedTouches, clientY, type } = event
      const yPos = type === 'touchmove' ? changedTouches[0].clientY : clientY

      if (!drawer || (type === 'mousemove' && !this.drag.dragging)) {
        return
      }

      const minHeight = window.innerHeight - drawer.dataset.height
      const newHeight = window.innerHeight - yPos

      drawer.style.transition = 'none'

      if (yPos <= minHeight && yPos >= 47) {
        drawer.style.height = `${newHeight}px`
      }
    },

    /**
     * Handle collapsing or expanding the drawer when the user stops holding
     * touch.
     * @param {Object} event - touchstart or mousedown events
     */
    onDragEnd(event) {
      const { drawer } = this.$refs
      const startY = this.drag.startY
      const { changedTouches, clientY, type } = event
      const newY = type === 'touchend' ? changedTouches[0].clientY : clientY
      const diff = newY - startY

      if (!drawer || (type === 'mouseup' && !this.drag.dragging)) {
        return
      }

      this.drag.dragging = false
      drawer.style.transition = ''

      if (diff > 0) {
        this.collapseDrawer()
      }
      else if (diff < 0) {
        this.openDrawer()
      }
    },

    /**
     * Update history with current state.
     */
    pushToHistory() {
      const state = { page: this.activePage }
      const url = `${this.$variable('routes.visualizer')}${`/${convertToHandle(this.activePage)}`}`
      window.history.pushState(state, '', url)
    },

    /**
     * Toggle filters open state.
     */
    toggleFilterState() {
      this.filtersOpen = !this.filtersOpen
    },

    /**
     * Handle apply filters.
     * - Filters are applied automatically so we only need to close the panel.
     */
    handleApplyFilters() {
      this.filtersOpen = false
    },
  },
}
</script>

<style lang="scss">
@import './bed-visualizer';
</style>
